import { Client } from '@urql/core';
import { AxiosRequestConfig, AxiosError } from 'axios';

import type { AuthenticationAdapter } from '@wetransfer/authentication.js';

import { createConfig } from '../config';
import {
  API_ENDPOINTS_WITHOUT_AUTH,
  API_ENDPOINTS_WITH_ID_TOKEN,
  AUTH_CALLBACK_PATH,
} from '../constants';
import { api as ApolloApi } from '@wetransfer/transfer/apollo-api/usage/api';
import getEnv from '@wetransfer/transfer/config/env';
import { PLAN_TIER } from '@wetransfer/transfer/constant/plan-tier';
import { removeCurrentTeamCookie } from '@wetransfer/transfer/hooks/useCurrentTeam/useCurrentTeam';
import {
  isBetaEnv,
  isLocalEnv,
  isTestEnv,
} from '@wetransfer/transfer/modules/url';

let auth0Module: undefined | AuthenticationAdapter;

export async function load(): Promise<AuthenticationAdapter> {
  if (auth0Module === undefined) {
    // We're loading the package async since we don't want to load this if not
    // absolutely needed (it forces https, which is not available in rspec)
    // As soon as our rspecs are replaced by StarShip we can normally load
    // this package without the need of async/optional loading.
    const { AuthenticationAdapter, AuthEnvironments } = await import(
      '@wetransfer/authentication.js'
    );

    let authEnv = AuthEnvironments.PROD;
    const config = createConfig();

    // It covers both local dev, test, stag and westage
    if (isLocalEnv() || isTestEnv() || isBetaEnv()) {
      authEnv = AuthEnvironments.STAG;
    }

    auth0Module = new AuthenticationAdapter({
      environment: authEnv,
      clientOptions: {
        auth0ClientOptions: {
          ...config,
          useRefreshTokens: true,
        },
      },
    });
  }

  return auth0Module;
}

export async function signUp({
  tier,
  search,
  lang,
  authPageParams,
}: {
  tier: PLAN_TIER;
  search: string;
  lang: string;
  authPageParams?: Record<string, unknown>;
}): Promise<void> {
  const auth0 = await load();
  await auth0.signUp({
    language: lang,
    appState: {
      tier,
    },
    search,
    ...(authPageParams && { authPageParams }),
  });
}

export async function login({
  search,
  lang,
  appState,
}: {
  search: string;
  lang: string;
  appState?: { [key: string]: unknown };
}): Promise<void> {
  const auth0 = await load();
  await auth0.login({
    search,
    language: lang,
    appState,
  });
}

export async function logout(returnDomain?: string): Promise<void> {
  const auth0 = await load();
  const config = createConfig();

  // This ensures that the redirect_uri is always the current origin
  const updatedConfig = {
    ...config,
    redirect_uri: `${location.origin}${AUTH_CALLBACK_PATH}`,
  };

  const returnTo = new URL(
    returnDomain === undefined
      ? updatedConfig.redirect_uri
      : `${location.protocol}//${returnDomain}`
  );

  if (location.port !== '') {
    returnTo.port = location.port;
  }

  auth0.logoutSession({ returnTo: returnTo.origin });

  removeCurrentTeamCookie();
}

export async function forgotPassword({
  lang,
}: {
  lang: string;
}): Promise<void> {
  const auth0 = await load();
  await auth0.forgotPassword({ language: lang });
}

export async function addAuthToApiRequestConfig({
  config,
  handleError,
  idToken,
  graphQLClient,
}: {
  config: AxiosRequestConfig;
  handleError: (error: AxiosError) => Promise<void>;
  idToken: string;
  graphQLClient: Client;
}): Promise<AxiosRequestConfig> {
  if (
    config.url === undefined ||
    !config.url.startsWith(getEnv().NEXT_PUBLIC_API_URL + '/api') ||
    API_ENDPOINTS_WITHOUT_AUTH.includes(config.url)
  ) {
    return config;
  }

  try {
    const auth0 = await load();
    ApolloApi.setGetAccessToken(getAccessToken);

    // fetch the token on each request (it is cached by auth0)
    const accessToken: string = await auth0.getTokenSilently();

    // add it to the current request
    config.headers.Authorization = `Bearer ${accessToken}`;

    // Add it to the GraphQL client. Setting up auth for GraphQL
    // here means that GraphQL requests will only use authentication
    // after the auth data is available.
    const fetchOptions = (graphQLClient.fetchOptions as () => RequestInit)();
    graphQLClient.fetchOptions = () => ({
      headers: {
        ...fetchOptions.headers,
        Authorization: `Bearer ${accessToken}`,
      },
    });

    // add id token to body
    if (API_ENDPOINTS_WITH_ID_TOKEN.includes(config.url)) {
      config.data = {
        ...(config.data ?? {}),
        id_token: idToken,
      };
    }
  } catch (error) {
    await handleError(error);
  }

  return config;
}

export async function getAccessToken(): Promise<string> {
  const auth0 = await load();

  return auth0.getTokenSilently();
}
